import { cabinetFromLine, soffitFromLine } from "building-generation/elements/caseworks/casework";
import type { Casework } from "building-types/casework/caseworkTypes";
import { PropInstanceHorizontalAlignment, PropInstanceLateralAlignment } from "building-types/datums/datumsTypesEnum";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Prop } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallStrip } from "building-types/walls/wallStrip";
import type { SolidWall } from "building-types/walls/wallTypes";
import { WALL_THICKNESS_INTERIOR } from "common/constants/buildingObjects";
import { ensureDefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { BuiltInInteriorPropId } from "doc/elements/propCatalog/interiorPropCatalog";
import { defaultPropLabels, propNew } from "doc/propTools";
import { propOffsetFromLine2 } from "doc/rooms/propPlacement";
import { polyLine2Centroid, polyLine2EdgeCount, polyLine2EdgeLength } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rangeNonInclusiveByCount } from "geom/range";
import { getWallPath } from "../walls/wall";
import { wallCenterLineAsPolyLine2 } from "../walls/wall";
import { WallStripImpl } from "../walls/wallStripImpl";
import type { RoomLayoutGenerator } from "./roomLayout";

const roomLayoutLiving: RoomLayoutGenerator[] = [
  {
    roomType: RoomType.DINING,
    async generate({ room }) {
      const { outline } = room;

      // dining set long axis is x
      const [cx, cy] = ensureDefined(polyLine2Centroid(outline));

      // rotate the dining set to align w/ long axis of room
      const rotate = polyLine2EdgeLength(outline, 0) > polyLine2EdgeLength(outline, 1) ? 0 : 90;

      return {
        props: [
          propNew({
            room,
            src: BuiltInInteriorPropId.DININGSET,
            x: cx,
            y: cy,
            z: room.level.floorElevation,
            rotationDegrees: rotate,
            catalogType: PropCatalogType.PROP_CATALOG,
            labels: defaultPropLabels(),
          }),
        ],
      };
    },
  },
  {
    roomType: RoomType.CABINETS,
    async generate({ room, wallIndex }) {
      const solidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.SOLID, true);
      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);
      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const hostLine = solidWallLines[0];
      // polyLine2Reverse(hostLine);

      const wallHalf = WALL_THICKNESS_INTERIOR / 2;
      const casework: Casework[] = [];
      casework.push(cabinetFromLine(room, hostLine, wallHalf, wallHalf, wallHalf, false));
      // casework.push(cabinetFromLine(room, hostLine, wallHalf, wallHalf, wallHalf, true));

      return {
        casework,
      };
    },
  },
  {
    roomType: RoomType.LIVING,
    generate: async ({ room, wallIndex, palette }) => {
      // handle the entertainment wall globally
      const props: Prop[] = [];

      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const solidWallGroups = wallIndex.wallStripsByPredicate(
        room,
        (wall) =>
          wall.wallType === WallType.SOLID && wall.doorPlacements.length === 0 && wall.windowPlacements.length === 0,
        true,
      );

      const solidWallLines = WallStripImpl.allEndPoints(solidWallGroups);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);

      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));
      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const rwalls = wallIndex.wallsInRoom(room);
      const windowWalls = rwalls.filter(
        (wall) => wall.wallType === WallType.SOLID && wall.windowPlacements.length > 0,
      ) as SolidWall[];
      windowWalls.sort((a, b) => getWallPath(b).length - getWallPath(a).length);

      const doorWalls = rwalls.filter(
        (wall) => wall.wallType === WallType.SOLID && wall.doorPlacements.length > 0,
      ) as SolidWall[];
      doorWalls.sort((a, b) => getWallPath(b).length - getWallPath(a).length);

      // layout couches
      for (const wall of voidWallLines) {
        let propId: BuiltInInteriorPropId | undefined;

        const wallLength = polyLine2EdgeLength(wall, 0);
        const offset = 12;

        if (wallLength > 180) {
          propId = BuiltInInteriorPropId.COUCH;
        } else if (wallLength > 100) {
          propId = BuiltInInteriorPropId.LOVESEAT;
        } else if (wallLength > 75) {
          propId = BuiltInInteriorPropId.ARMCHAIR;
        }

        if (!propId) {
          continue;
        }

        const couch = await propOffsetFromLine2({
          settings: palette,
          propId: propId,
          room,
          offset: offset,
          edge: wall,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(couch);
      }

      if (solidWallLines.length > 0 && polyLine2EdgeLength(solidWallLines[0], 0) > 60) {
        const tvWall = solidWallLines[0];

        const tv = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.TV,
          room,
          offset: 5,
          edge: tvWall,
          z: room.level.floorElevation + 45,
          lateralAlignment: PropInstanceLateralAlignment.CENTERLINE,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        const tvCabinet = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.TVCABINET,
          room,
          offset: 5,
          edge: tvWall,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(tv, tvCabinet);
      }

      return {
        props,
      };
    },
  },
  {
    roomType: RoomType.FORMAL_LIVING,
    generate: async ({ room, palette, wallIndex }) => {
      // handle the entertainment wall globally
      const props: Prop[] = [];
      const tvWall = false;

      const strip0 = wallIndex.wallStripFromRoomEdgeIndex(room, 0);
      const strip1 = wallIndex.wallStripFromRoomEdgeIndex(room, 1);
      const strip2 = wallIndex.wallStripFromRoomEdgeIndex(room, 2);
      const strip3 = wallIndex.wallStripFromRoomEdgeIndex(room, 3);

      const strips = [strip0, strip1, strip2, strip3];

      const solidWallLines: PolyLine2[] = [];
      const voidWallLines: PolyLine2[] = [];
      const solidWallNoWindowLines: PolyLine2[] = [];

      for (let i = 0; i < strips.length; i++) {
        const strip = strips[i];

        if (!strip) {
          continue;
        }

        const stripWalls = strip.toWallArray();
        for (const wall of stripWalls) {
          if (wall.wallType === WallType.VOID) {
            voidWallLines.push(strip.baseline(true));
          }
          if (wall.wallType === WallType.SOLID) {
            solidWallLines.push(strip.baseline(true));
          }
          if (wall.wallType === WallType.SOLID && wall.windowPlacements.length === 0) {
            solidWallNoWindowLines.push(strip.baseline(true));
          }
        }
      }

      solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));
      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));
      solidWallNoWindowLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      for (let i = 0; i < solidWallLines.length; i++) {
        if (i !== 0 || !tvWall) {
          let propId: BuiltInInteriorPropId | undefined;
          const wallLength = polyLine2EdgeLength(solidWallLines[i], 0);
          const offset = 12;

          if (wallLength > 180) {
            propId = BuiltInInteriorPropId.COUCH;
          } else if (wallLength > 100) {
            propId = BuiltInInteriorPropId.LOVESEAT;
          } else if (wallLength > 75) {
            propId = BuiltInInteriorPropId.ARMCHAIR;
          }

          if (!propId) {
            continue;
          }

          const couch = await propOffsetFromLine2({
            settings: palette,
            propId: propId,
            room,
            offset: offset,
            edge: solidWallLines[i],
            z: room.level.floorElevation,
            horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
            catalogType: PropCatalogType.PROP_CATALOG,
            labels: defaultPropLabels(),
          });

          props.push(couch);
        }
      }

      return {
        props,
      };
    },
  },
  {
    roomType: RoomType.OFFICE,
    async generate({ room, wallIndex, palette }) {
      let doorIndex = 0;
      let oppIndex = 2;
      let doorNextIndex = 1;

      const strip0 = wallIndex.wallStripFromRoomEdgeIndex(room, 0);
      const strip1 = wallIndex.wallStripFromRoomEdgeIndex(room, 1);
      const strip2 = wallIndex.wallStripFromRoomEdgeIndex(room, 2);
      const strip3 = wallIndex.wallStripFromRoomEdgeIndex(room, 3);

      const strips = [strip0, strip1, strip2, strip3];

      let testDoors: GeneratedDoorPlacement[] = [];

      for (let i = 0; i < strips.length; i++) {
        const strip = strips[i];
        if (!strip) {
          continue;
        }
        testDoors = strip.doors();
        if (testDoors.length > 0) {
          doorIndex = i;
          oppIndex = (doorIndex + 4 + 2) % 4;
          doorNextIndex = (doorIndex + 4 + 1) % 4;
        }
      }

      const oppLine = strips[oppIndex]?.baseline(true);
      const doorNextLine = strips[doorNextIndex]?.baseline(true);
      const props: Prop[] = [];

      if (oppLine && doorNextLine) {
        let offset = 24;
        const oppLen = polyLine2EdgeLength(oppLine, 0);
        let deskParam = oppLen / 2;

        if (oppLen < 108 || polyLine2EdgeLength(doorNextLine, 0) < 108) {
          offset = 0;
          deskParam = oppLen - 36;
        }

        const desk = await propOffsetFromLine2({
          settings: palette,
          propId: BuiltInInteriorPropId.DESKSET,
          room,
          offset: offset,
          edge: oppLine,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          param: deskParam,
          paramNormalized: false,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(desk);
      }

      return { props, casework: [] };
    },
  },
  {
    roomType: RoomType.LIBRARY,
    async generate({ room, wallIndex, palette }) {
      const solidWallLines = getSolidWallLines(room, wallIndex);

      const props: Prop[] = [];

      for (const line of solidWallLines) {
        const len = polyLine2EdgeLength(line, 0);
        const divs = rangeNonInclusiveByCount(0, len, Math.floor(len / 34));

        for (let div of divs) {
          div = div / len;
          const bookshelf = await propOffsetFromLine2({
            settings: palette,
            propId: BuiltInInteriorPropId.BOOKSHELF6,
            room,
            offset: 0,
            edge: line,
            z: room.level.floorElevation,
            horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
            param: div,
            paramNormalized: true,
            catalogType: PropCatalogType.PROP_CATALOG,
            labels: defaultPropLabels(),
          });

          props.push(bookshelf);
        }
      }

      return { props, casework: [] };
    },
  },
  {
    roomType: RoomType.READING_NOOK,
    async generate({ room, wallIndex, palette }) {
      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);

      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const casework: Casework[] = [];

      for (const line of voidWallLines) {
        casework.push(soffitFromLine(room, line, 0, 0, 0));
      }

      const rwalls = wallIndex.wallsInRoom(room);
      const windowWalls = rwalls.filter((wall) => wall.wallType === WallType.SOLID && wall.windowPlacements.length > 0);
      windowWalls.sort((a, b) => getWallPath(b).length - getWallPath(a).length);

      const props: Prop[] = [];

      const hostWall = windowWalls[0];
      let hostLine;

      // place seating based on wall size

      if (hostWall) {
        hostLine = wallCenterLineAsPolyLine2(room, hostWall);
      } else {
        return null;
      }

      const wallLength = polyLine2EdgeLength(hostLine, 0);

      let propId: BuiltInInteriorPropId | undefined;

      if (wallLength > 150) {
        propId = BuiltInInteriorPropId.COUCH;
      } else if (wallLength > 100) {
        propId = BuiltInInteriorPropId.LOVESEAT;
      } else if (wallLength > 75) {
        propId = BuiltInInteriorPropId.ARMCHAIR;
      }

      if (propId) {
        const couch = await propOffsetFromLine2({
          settings: palette,
          propId,
          room,
          offset: 12,
          edge: hostLine,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(couch);
      }

      return {
        props,
        casework,
      };
    },
  },
  {
    roomType: RoomType.DINING_NOOK,
    async generate({ room, wallIndex, palette: settings }) {
      const { outline } = room;

      if (polyLine2EdgeCount(outline) !== 4) {
        return null;
      }

      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);

      const casework: Casework[] = [];

      for (const line of voidWallLines) {
        casework.push(soffitFromLine(room, line, 0, 0, 0));
      }

      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const props: Prop[] = [];

      if (voidWallLines.length > 0) {
        const hostLine = voidWallLines[0];
        const hostLen = polyLine2EdgeLength(hostLine, 0);

        const table = await propOffsetFromLine2({
          settings,
          propId: BuiltInInteriorPropId.TABLE6,
          room,
          offset: 12,
          edge: hostLine,
          param: 0.5,
          paramNormalized: true,
          z: room.level.floorElevation,
          // propRotate: degreeToRadian(-90),
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        const seat = await propOffsetFromLine2({
          settings,
          propId: BuiltInInteriorPropId.DININGCHAIR,
          room,
          offset: 30,
          edge: hostLine,
          param: hostLen / 2 + 38,
          paramNormalized: false,
          z: room.level.floorElevation,
          rotate: degreeToRadian(90),
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          lateralAlignment: PropInstanceLateralAlignment.CENTERLINE,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        const seat2 = await propOffsetFromLine2({
          settings,
          propId: BuiltInInteriorPropId.DININGCHAIR,
          room,
          offset: 30,
          edge: hostLine,
          param: hostLen / 2 - 38,
          paramNormalized: false,
          z: room.level.floorElevation,
          rotate: degreeToRadian(-90),
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          lateralAlignment: PropInstanceLateralAlignment.CENTERLINE,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(table, seat, seat2);

        return {
          props,
          casework,
        };
      } else {
        return {};
      }
    },
  },
];

export default roomLayoutLiving;

function getSolidWallLines(room: Room, wallIndex: WallIndex): PolyLine2[] {
  const strip0 = wallIndex.wallStripFromRoomEdgeIndex(room, 0);
  const strip1 = wallIndex.wallStripFromRoomEdgeIndex(room, 1);
  const strip2 = wallIndex.wallStripFromRoomEdgeIndex(room, 2);
  const strip3 = wallIndex.wallStripFromRoomEdgeIndex(room, 3);

  const strips = [strip0, strip1, strip2, strip3].filter((x) => !!x) as WallStrip[];

  // where are the solid walls?
  const solidWallLines: PolyLine2[] = [];

  for (const strip of strips) {
    if (strip && strip.walls[strip.startIndex].wallType === WallType.SOLID) {
      const wall = strip.walls[strip.startIndex] as SolidWall;
      if (wall.doorPlacements.length === 0 && wall.windowPlacements.length === 0) {
        solidWallLines.push(strip.baseline(true));
      }
    }
  }

  solidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

  return solidWallLines;
}
